import React from 'react'
import styled, { css } from 'styled-components'
import theme from 'stories/utils/theme'
import { Body1, ButtonText, Headline1, Headline3 } from 'stories/elements/Typography/Text'
import Link from 'next/link'
import GreenCheck from 'assets/icons/green-check.svg'
import BackgroundPatternIcon from 'assets/about-tutoring/tutoring-pattern.svg'
import SmallerBackgroundPatternIcon from 'assets/about-tutoring/smaller-tutoring-pattern.svg'
import { deviceSize } from 'lib/constants/GridBreakpoints'
import useMedia from 'lib/hooks/useMedia'
import VideoPlayer from 'components/modules/Players/VideoPlayer'
import { trackEvent } from 'lib/tracking/analytics-service'
import { AnalyticsEventLandingPageButtonClick } from 'lib/constants/AnalyticsEventType'
import { PrismicImage } from 'interfaces/GeneratedLandingPage'
import DynamicButton from './DynamicButton'

const PRIMARY_BUTTON_WIDTH = 280

const Container = styled.div<{ backgroundColor?: string; useLandingPageHeaderFont?: boolean }>`
    width: 100%;
    height: 750px;
    background-color: ${({ backgroundColor }) => backgroundColor ?? theme.colors.accentBlue};
    overflow: hidden;
    position: relative;
    display: grid;
    grid-template-columns: 1fr 1.5fr;
    place-content: center;
    align-items: center;
    & h1 {
        ${({ useLandingPageHeaderFont }) =>
            !useLandingPageHeaderFont
                ? css`
                      font-size: 36px;
                      line-height: 54px;
                  `
                : ''}
        @media (max-width: 1150px) {
            ${({ useLandingPageHeaderFont }) =>
                !useLandingPageHeaderFont
                    ? css`
                          font-size: 30px;
                          font-weight: 700;
                          line-height: 30px;
                      `
                    : ''}
            text-align: center;
            overflow-wrap: break-word;
            padding: 0 15px;
        }
    }
    @media (max-width: 1150px) {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        height: 100%;
        padding-top: 50px;
    }
`

const TextsWrapper = styled.div`
    display: grid;
    grid-gap: 10px;
    padding-left: 25%;
    @media (max-width: 1150px) {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        flex-wrap: wrap;
        padding: 5% 10%;
        grid-gap: 10px;
        p {
            text-align: center;
        }
    }
    @media (max-width: 750px) {
        padding: 10% 5%;
        h1,
        h3 {
            text-align: center;
        }
    }
`

const VideoContainer = styled.div`
    width: 100%;
    display: grid;
    padding: 30px 15% 0 15%;
    place-content: center;
    align-items: center;
    position: relative;
    video {
        border-radius: ${theme.borderRadius.normal};
        z-index: 1;
    }
    @media (max-width: 1150px) {
        padding-top: 25px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        p {
            padding: 0 10%;
            width: 100%;
            text-align: center;
        }
    }
    @media (max-width: 750px) {
        padding: 25px 0;
        place-content: start;
    }
`

const VideoFrame = styled.img`
    position: absolute;
    left: 0;
    right: 0;
    margin: 0 auto;
    @media (max-width: 750px) {
        display: none;
    }
`

const PatternBackground = styled(BackgroundPatternIcon)`
    position: absolute;
    right: 15%;
    top: 50px;
    @media (max-width: 1150px) {
        top: 150px;
    }
    @media (max-width: 750px) {
        display: none;
    }
`

const SmallerPatternBackground = styled(SmallerBackgroundPatternIcon)`
    position: absolute;
    left: 15%;
    bottom: 25px;
    @media (max-width: 750px) {
        display: none;
    }
`

const ButtonWrapper = styled.div<{ onlyPrimary?: boolean }>`
    display: grid;
    padding-top: 50px;
    grid-template-columns: ${({ onlyPrimary }) => (onlyPrimary ? '1fr' : '1fr 1fr')};
    align-items: center;
    grid-gap: 10px;
    a {
        position: relative;
    }
    .stempel {
        position: absolute;
        object-position: right;
        top: -35px;
        right: -${PRIMARY_BUTTON_WIDTH + 35}px;
    }
    @media (max-width: 1150px) {
        grid-gap: 10px;
        a,
        span {
            width: 100% !important;
        }
        .stempel {
            display: none;
        }
    }
`

const HeaderInfoWrapper = styled.div`
    display: grid;
    grid-template-columns: auto 1fr;
    grid-gap: 10px;
    position: relative;
    svg {
        position: relative;
        top: 2px;
    }
    @media (max-width: 1150px) {
    }
`

interface Props {
    title: string
    text: string
    advantages: string[]
    primaryButtonText: string
    primaryButtonTarget: string
    secondaryButtonText: string
    secondaryButtonTarget: string
    videoSource: string
    eventTrackingValue?: string
    onPrimaryButtonClick?: () => void
    useLandingPageHeaderFont?: boolean
    backgroundColor?: string
    textColor?: string
    primaryButtonColor?: string
    videoFrame?: PrismicImage
    isHeader?: boolean
    buttonStempel?: PrismicImage
}

const HeaderVideo = ({
    title,
    text,
    advantages,
    primaryButtonText,
    primaryButtonTarget,
    secondaryButtonText,
    secondaryButtonTarget,
    videoSource,
    eventTrackingValue,
    onPrimaryButtonClick,
    useLandingPageHeaderFont,
    backgroundColor,
    textColor,
    primaryButtonColor,
    videoFrame,
    isHeader,
    buttonStempel,
}: Props) => {
    const md = useMedia(deviceSize.md)

    const handlePrimaryButtonClick = () => {
        eventTrackingValue && trackEvent(AnalyticsEventLandingPageButtonClick, { value: eventTrackingValue })

        if (onPrimaryButtonClick) {
            onPrimaryButtonClick()
        }
    }

    const isOnlyPrimaryButton = !(secondaryButtonText && secondaryButtonTarget)
    const advantagesPresent = !!advantages?.length
    return (
        <Container backgroundColor={backgroundColor} useLandingPageHeaderFont={useLandingPageHeaderFont}>
            <TextsWrapper>
                {isHeader ? (
                    <Headline1 marginBottom={25} color={textColor ?? theme.colors.white}>
                        {title}
                    </Headline1>
                ) : (
                    <Headline3 marginBottom={25} color={textColor ?? theme.colors.white}>
                        {title}
                    </Headline3>
                )}

                {text ? (
                    <Body1 color={textColor ?? theme.colors.white} marginBottom={advantagesPresent ? 20 : 0}>
                        {text}
                    </Body1>
                ) : null}
                {advantagesPresent &&
                    advantages.map((text, i) => (
                        <HeaderInfoWrapper key={`header-info-${i}`}>
                            <GreenCheck />
                            <Body1 color={textColor ?? theme.colors.caption}>{text}</Body1>
                        </HeaderInfoWrapper>
                    ))}
                <ButtonWrapper onlyPrimary={isOnlyPrimaryButton}>
                    {primaryButtonText && primaryButtonTarget ? (
                        <span onClick={handlePrimaryButtonClick}>
                            <DynamicButton
                                width={PRIMARY_BUTTON_WIDTH}
                                height={60}
                                fullWidth
                                color={primaryButtonColor ?? theme.colors.knowunityBlue}
                                href={primaryButtonTarget}
                                borderRadius={theme.borderRadius.normal}
                            >
                                <ButtonText color={theme.colors.white}>{primaryButtonText}</ButtonText>
                                {buttonStempel?.url ? (
                                    <img
                                        className="stempel"
                                        src={buttonStempel.url}
                                        alt={buttonStempel.alt ?? 'button stempel'}
                                        width={buttonStempel?.dimensions.width}
                                        height={buttonStempel?.dimensions.width}
                                    />
                                ) : null}
                            </DynamicButton>
                        </span>
                    ) : null}
                    {secondaryButtonText && secondaryButtonTarget ? (
                        <Link href={secondaryButtonTarget} prefetch={false}>
                            <ButtonText color={theme.colors.knowunityBlue} textAlign="center">
                                {secondaryButtonText}
                            </ButtonText>
                        </Link>
                    ) : null}
                </ButtonWrapper>
            </TextsWrapper>
            <VideoContainer>
                {!videoFrame?.url ? <PatternBackground /> : null}
                <VideoPlayer
                    width={md ? 375 : 291}
                    height={md ? 667 : 517}
                    controls
                    source={videoSource}
                    sourceType="video/mp4"
                    autoPlay
                    muted
                    replay
                    border={`3px solid ${theme.colors.accentBlue}`}
                />
                {videoFrame?.url ? (
                    <VideoFrame
                        src={videoFrame.url}
                        alt={videoFrame?.alt ?? 'video frame'}
                        height={videoFrame?.dimensions?.height}
                        width={videoFrame?.dimensions.width}
                    />
                ) : (
                    <>
                        <PatternBackground />
                        <SmallerPatternBackground />
                    </>
                )}
            </VideoContainer>
        </Container>
    )
}

export default HeaderVideo
